import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SectionNav from '../../../components/layout/SectionNav';
import { researchTabs } from '../../../nav/publicNav';
import { Loader2, UserSearch, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { useInsiderScreenerStore } from '../../../state/insiderScreenerStore';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import ExportButton from '../../../components/ui/ExportButton';

const CATEGORY_COLORS = {
  promoter: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  kmp: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  director: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
  sast: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
};

function categoryColor(cat) {
  if (!cat) return 'bg-muted text-muted-foreground';
  const lc = cat.toLowerCase();
  for (const [key, cls] of Object.entries(CATEGORY_COLORS)) {
    if (lc.includes(key)) return cls;
  }
  return 'bg-muted text-muted-foreground';
}

export default function InsiderScreenerPage() {
  const navigate = useNavigate();
  const {
    items, total, page, pageSize, categories, loading, error, filters,
    setFilters, fetch: fetchData,
  } = useInsiderScreenerStore();

  useEffect(() => { fetchData(); }, []);

  const applyFilters = () => fetchData(1);
  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-4">
      <SectionNav tabs={researchTabs} />
      <div>
        <h1 className="text-xl font-bold flex items-center gap-2">
          <UserSearch className="w-5 h-5 text-emerald-500" /> Insider Trading Screener
        </h1>
        <p className="text-xs text-muted-foreground">Who's buying? Filter insider announcements across the market.</p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-3 flex flex-wrap gap-2 items-end">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase text-muted-foreground font-medium">Search</label>
            <input
              type="text"
              placeholder="Company or symbol..."
              value={filters.search}
              onChange={(e) => setFilters({ search: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
              className="h-8 px-3 text-xs rounded-md border border-border bg-background focus:ring-2 focus:ring-emerald-500/50 focus:outline-none w-48"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase text-muted-foreground font-medium">Category</label>
            <select
              value={filters.category}
              onChange={(e) => setFilters({ category: e.target.value })}
              className="h-8 px-2 text-xs rounded-md border border-border bg-background focus:ring-2 focus:ring-emerald-500/50 focus:outline-none"
            >
              <option value="">All</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase text-muted-foreground font-medium">From</label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters({ dateFrom: e.target.value })}
              className="h-8 px-2 text-xs rounded-md border border-border bg-background focus:outline-none"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase text-muted-foreground font-medium">To</label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters({ dateTo: e.target.value })}
              className="h-8 px-2 text-xs rounded-md border border-border bg-background focus:outline-none"
            />
          </div>
          <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={applyFilters}>
            Search
          </Button>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <div className="text-red-500 text-sm p-4 bg-red-50 dark:bg-red-950/20 rounded-lg">{error}</div>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">{total.toLocaleString()} results</span>
            <ExportButton rows={items} filename="insider-trades" />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-emerald-800 text-white">
                  <th className="text-left py-2 px-3 font-semibold text-[10px] uppercase">Date</th>
                  <th className="text-left py-2 px-3 font-semibold text-[10px] uppercase">Company</th>
                  <th className="text-left py-2 px-3 font-semibold text-[10px] uppercase">Symbol</th>
                  <th className="text-left py-2 px-3 font-semibold text-[10px] uppercase">Category</th>
                  <th className="text-left py-2 px-3 font-semibold text-[10px] uppercase">Headline</th>
                  <th className="text-center py-2 px-3 font-semibold text-[10px] uppercase">Link</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r, i) => (
                  <tr key={i} className="border-b border-muted/20 hover:bg-muted/20">
                    <td className="py-2 px-3 whitespace-nowrap text-muted-foreground">
                      {r.date ? new Date(r.date).toLocaleDateString('en-IN') : '—'}
                    </td>
                    <td className="py-2 px-3 font-medium">
                      <button
                        className="hover:text-emerald-600 hover:underline text-left"
                        onClick={() => r.symbol && navigate(`/public/india-company/${r.symbol}`)}
                      >
                        {r.company_name}
                      </button>
                    </td>
                    <td className="py-2 px-3">{r.symbol || '—'}</td>
                    <td className="py-2 px-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${categoryColor(r.category)}`}>
                        {r.category}
                      </span>
                    </td>
                    <td className="py-2 px-3 max-w-xs truncate" title={r.headline}>
                      {r.headline || '—'}
                    </td>
                    <td className="py-2 px-3 text-center">
                      {r.attachment_url && (
                        <a href={r.attachment_url} target="_blank" rel="noopener noreferrer"
                          className="text-emerald-600 hover:text-emerald-500">
                          <ExternalLink className="w-3.5 h-3.5 inline" />
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-12 text-center text-muted-foreground">
                      No insider announcements found matching your filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1}
                onClick={() => fetchData(page - 1)}>
                <ChevronLeft className="w-3.5 h-3.5" />
              </Button>
              <span className="text-xs text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <Button variant="outline" size="sm" disabled={page >= totalPages}
                onClick={() => fetchData(page + 1)}>
                <ChevronRight className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
